import { useEffect, useCallback, useState } from 'react'
import StorageService from '../services/storage'
import { useAppDispatch, useAppSelector } from './reduxHooks'
import { logout } from '../state/thunks/authThunk'

type MessageType =
  | 'get-auth-token'
  | 'app-logout'
  | 'report-created'
  | 'show-ui'

interface IframeMessage {
  type: MessageType
  id: string
  data?: any
}

interface IframeResponse {
  id: string
  data: any
  error?: string
}

export interface IframeShowUIArgs {
  name: 'unpaid-bills'
  args: any
}

export const useIframeMessages = (handlers?: {
  [key: string]: (data: any) => void
}) => {
  const dispatch = useAppDispatch()
  const { user, account } = useAppSelector((state) => ({
    user: state.auth.user,
    account: state.auth.account,
  }))

  const [connectedIframes, setConnectedIframes] = useState<Window[]>([])
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      const message = event.data as IframeMessage
      if (!message || !message.type || !message.id) return

      switch (message.type) {
        case 'get-auth-token':
          try {
            const token = StorageService.getAuthToken()
            const response: IframeResponse = {
              id: message.id,
              data: { token, user, account },
            }
            ;(event.source as any)?.postMessage(response, {
              targetOrigin: '*',
            })
          } catch (error) {
            const errorResponse: IframeResponse = {
              id: message.id,
              data: null,
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get auth token',
            }
            ;(event.source as any)?.postMessage(errorResponse, {
              targetOrigin: '*',
            })
          }
          break

        case 'app-logout':
          try {
            dispatch(logout())
            const response: IframeResponse = {
              id: message.id,
              data: { success: true },
            }
            ;(event.source as any)?.postMessage(response, {
              targetOrigin: '*',
            })
          } catch (error) {
            const errorResponse: IframeResponse = {
              id: message.id,
              data: null,
              error:
                error instanceof Error ? error.message : 'Failed to logout',
            }
            ;(event.source as any)?.postMessage(errorResponse, {
              targetOrigin: '*',
            })
          }
          break
        case 'show-ui':
          // eslint-disable-next-line no-case-declarations
          console.log('received show-ui message', message)
          // eslint-disable-next-line no-case-declarations
          const handler = handlers?.['show-ui']
          if (handler) {
            handler(message.data as IframeShowUIArgs)
          }
          break

        default:
          console.warn('Unhandled iframe message type:', message.type)
      }
    },
    [dispatch, user],
  )

  const registerIframe = useCallback((iframeWindow: Window) => {
    setConnectedIframes((prev) => [...prev, iframeWindow])
  }, [])

  const sendMessageToIframes = useCallback(
    (message: any) => {
      connectedIframes.forEach((iframe) => {
        iframe.postMessage(message, '*')
      })
    },
    [connectedIframes],
  )

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  return {
    registerIframe,
    sendMessageToIframes,
  }
}
